import { Cartesian3 } from "cesium";
import { EarthBrainViewerAPIs } from "./EarthBrainViewerAPIs";
/**
 * API class for solid mesh operations
 */
export class SolidMeshAPIs extends EarthBrainViewerAPIs {
    /**
     * Starts solid mesh construction
     * @param options - Callback and options for construction process
     */
    startSolidMeshConstruction(options) {
        const shapeType = options.shapeType;
        this._scDesign.startSolidMeshConstruction(options, shapeType, options.shapeFactory);
    }
    /**
     * Starts multi-point solid mesh construction.
     * After each click, `onProgress` is called with `{ step, localPosition }`.
     * After all points are collected, a temp mesh is created at the first point
     * and `onFinish` is called with `(solidMeshIds, { positions })`.
     * Right-click undoes the last picked point.
     *
     * @param options - shapeType, pointCount and callback handlers
     */
    startMultiPointConstruction(options) {
        const shapeType = options.shapeType ?? "box";
        const pointCount = options.pointCount ?? 2;
        this._scDesign.startMultiPointSolidMeshConstruction(options, shapeType, pointCount, options.shapeFactory);
    }
    /**
     * Finishes solid mesh construction
     */
    finishSolidMeshConstruction() {
        this._scDesign.finishSolidMeshConstruction();
    }
    /**
     * Cancels solid mesh construction
     */
    cancelSolidMeshConstruction() {
        this._scDesign.cancelSolidMeshConstruction();
    }
    /**
     * Checks if solid mesh is under construction
     * @returns True if under construction
     */
    checkSolidMeshUnderConstruction() {
        return this._scDesign.checkSolidMeshUnderConstruction();
    }
    /**
     * Sets shape type during construction
     * @param shapeType - Shape type ("box", "sphere", or "cylinder")
     */
    setSolidMeshConstructionShapeType(shapeType) {
        this._scDesign.setSolidMeshConstructionShapeType(shapeType);
    }
    /**
     * Deletes a solid mesh
     * @param solidMeshId - Solid mesh ID
     */
    deleteSolidMesh(solidMeshId) {
        this._scDesign.deleteSolidMesh(solidMeshId);
    }
    /**
     * Sets solid mesh visibility
     * @param solidMeshId - Solid mesh ID
     * @param visible - True to show
     */
    setSolidMeshVisibility(solidMeshId, visible) {
        this._scDesign.setSolidMeshVisibility(solidMeshId, visible);
    }
    /**
     * Fits camera to solid mesh
     * @param solidMeshId - Solid mesh ID
     */
    fitToSolidMesh(solidMeshId) {
        this._scDesign.fitToSolidMesh(solidMeshId);
    }
    /**
     * Starts solid mesh editing
     * @param solidMeshId - Solid mesh ID
     * @param callback - Callback for edit process
     */
    startSolidMeshEdit(solidMeshId, callback) {
        this._scDesign.startSolidMeshEdit(solidMeshId, callback);
    }
    /**
     * Finishes solid mesh editing.
     * @returns Resolves after the onFinish callback has completed
     */
    async finishSolidMeshEdit() {
        await this._scDesign.finishSolidMeshEdit();
    }
    /**
     * Cancels solid mesh editing
     */
    cancelSolidMeshEdit() {
        this._scDesign.cancelSolidMeshEdit();
    }
    /**
     * Checks if solid mesh is under edit
     * @returns True if under edit
     */
    isSolidMeshUnderEdit() {
        return this._scDesign.isSolidMeshUnderEdit();
    }
    /**
     * Rotates the solid mesh currently under edit
     * @param angles - Rotation angles [roll(X), pitch(Y), heading(Z)] in degrees
     * @remarks Only works when a mesh is being edited via startSolidMeshEdit(). No-op if no mesh under edit.
     */
    rotateSolidMesh(angles) {
        this._scDesign.rotateSolidMesh(angles);
    }
    /**
     * Rotates solid mesh relative to its baseline (creation/load-time) orientation.
     * @param solidMeshId - Solid mesh ID
     * @param delta - Delta rotation [roll, pitch, heading] in degrees
     */
    rotateSolidMeshFromBaseline(solidMeshId, delta) {
        this._scDesign.rotateSolidMeshFromBaseline(solidMeshId, delta);
    }
    /**
     * Scales the solid mesh currently under edit
     * @param scale - Scale values [x, y, z]
     * @remarks Only works when a mesh is being edited via startSolidMeshEdit(). No-op if no mesh under edit.
     */
    scaleSolidMesh(scale) {
        this._scDesign.scaleSolidMesh(scale);
    }
    /**
     * Sets solid mesh color
     * @param solidMeshId - Solid mesh ID
     * @param color - Color [r, g, b] (0-1)
     */
    setSolidMeshColor(solidMeshId, color) {
        this._scDesign.setSolidMeshColor(solidMeshId, color);
    }
    /**
     * Sets solid mesh transparency
     * @param solidMeshId - Solid mesh ID
     * @param transparency - Transparency value (0-1)
     */
    setSolidMeshTransparency(solidMeshId, transparency) {
        this._scDesign.setSolidMeshTransparency(solidMeshId, transparency);
    }
    /**
     * Sets solid mesh anchor type.
     * @param solidMeshId - Solid mesh ID
     * @param anchorType - Anchor type (including edge-midpoint for simple structures)
     * @param options.preservePosition - If true, adjusts translation so the mesh stays in the same world position after anchor change
     */
    async setSolidMeshAnchorType(solidMeshId, anchorType, options) {
        await this._scDesign.setSolidMeshAnchorType(solidMeshId, anchorType, options);
    }
    /**
     * Undo the last picked point during multi-point solid mesh construction.
     * UI should call this when the user selects Undo from the context menu.
     */
    undoLastPickedPoint() {
        this._scDesign.undoLastPickedSolidMeshPoint();
    }
    /**
     * Returns the number of points currently picked in multi-point solid mesh
     * construction.
     */
    getPickedPointCount() {
        return this._scDesign.getSolidMeshPickedPointCount();
    }
    /**
     * Sets solid mesh position in local coordinates.
     * @param solidMeshId - Solid mesh ID
     * @param position - Local position (anchor based on anchorType) [x, y, z]
     */
    async setLocalPosition(solidMeshId, position) {
        const cartesian = new Cartesian3(position[0], position[1], position[2]);
        await this._scDesign.setLocalPosition(solidMeshId, cartesian);
    }
    /**
     * Gets solid mesh SavedData (shapeConfig + props)
     * @param solidMeshId - Solid mesh ID
     * @returns TSolidMeshSavedData
     */
    getSolidMeshJSON(solidMeshId) {
        return this._scDesign.getSolidMeshJSON(solidMeshId);
    }
    /**
     * Gets solid mesh rotation in degrees
     * @param solidMeshId - Solid mesh ID
     * @param options.reference - Reference angles to find closest values to (optional)
     * @param options.relativeToBaseline - If true, returns delta from baseline
     * @returns Rotation angles for each axis in degrees
     */
    getSolidMeshRotationDegrees(solidMeshId, options) {
        return this._scDesign.getSolidMeshRotationDegrees(solidMeshId, options);
    }
    /**
     * Resets solid mesh rotation to baseline (creation/load-time orientation).
     * @param solidMeshId - Solid mesh ID
     */
    resetSolidMeshRotationToBaseline(solidMeshId) {
        this._scDesign.resetSolidMeshRotationToBaseline(solidMeshId);
    }
    /**
     * Gets solid mesh anchor point in local coordinates
     * @param solidMeshId - Solid mesh ID
     * @returns Local anchor position
     */
    async getSolidMeshAnchorLocal(solidMeshId) {
        return await this._scDesign.getSolidMeshAnchorLocal(solidMeshId);
    }
    /**
     * Loads solid mesh from validated SavedData
     * @param data - TSolidMeshSavedData (validated by Client)
     * @returns { id: string }
     */
    async loadSolidMeshJSON(data) {
        return await this._scDesign.loadSolidMeshJSON(data);
    }
    /**
     * Applies preview-only transform in-place (no primitive dispose+rebuild).
     * Does NOT require edit mode. Call this to update a temporary mesh
     * while the user is editing parameters in the creation dialog.
     * @param solidMeshId - Solid mesh ID
     * @param options.dimensions - Target dimensions; converted to scale internally
     * @param options.scale - Absolute scale {x, y, z} (ignored if dimensions provided)
     * @param options.localCenter - New local-space center {x, y, z}
     * @param options.hprRad - Optional HPR rotation in radians {heading, pitch, roll}
     */
    async applyPreviewParams(solidMeshId, options) {
        await this._scDesign.applyPreviewParams(solidMeshId, options);
    }
    /**
     * Atomically updates a retaining wall from its two top-edge basis points.
     * The full target state (rotation, scale, position, bottomZ) is computed
     * from the input alone and applied in one shot — deterministic and
     * reversible, no post-hoc anchor correction.
     * @param solidMeshId - Solid mesh ID
     * @param params - Two basis points (top edge), vertical height, gradient %
     * @returns The geometry applied to the mesh
     */
    async updateRetainingWallFromTwoPoints(solidMeshId, params) {
        return await this._scDesign.updateRetainingWallFromTwoPoints(solidMeshId, params);
    }
    /**
     * Atomically updates a pipe from its two axis endpoints.
     * Same contract as `updateRetainingWallFromTwoPoints`: full target state
     * computed from the input alone and applied in one shot.
     * @param solidMeshId - Solid mesh ID
     * @param params - Two axis endpoints and diameter (mm)
     * @returns The geometry applied to the mesh
     */
    async updatePipeFromTwoPoints(solidMeshId, params) {
        return await this._scDesign.updatePipeFromTwoPoints(solidMeshId, params);
    }
    /**
     * Reconstructs the two basis points of a two-point structure from the
     * authoritative quaternion (no Euler decomposition), plus the
     * baseline-relative horizontal bearing for the azimuth display.
     * @param solidMeshId - Solid mesh ID
     * @param axis - Object-local extension axis: "z" for pipe, "x" for retaining-wall
     */
    async getSolidMeshLocalEndpoints(solidMeshId, axis) {
        return await this._scDesign.getSolidMeshLocalEndpoints(solidMeshId, axis);
    }
    /**
     * Gets solid mesh geometry data
     * @param solidMeshId - Solid mesh ID
     * @returns Vertices and indices arrays
     */
    getSolidMeshGeometry(solidMeshId) {
        return this._scDesign.getSolidMeshGeometry(solidMeshId);
    }
    /**
     * Updates attributes on an existing solid mesh (e.g. bottomZ after dialog edits).
     * @param solidMeshId - Solid mesh ID
     * @param attrs - Partial attributes to merge
     */
    updateSolidMeshAttributes(solidMeshId, attrs) {
        this._scDesign.updateSolidMeshAttributes(solidMeshId, attrs);
    }
}
//# sourceMappingURL=solid-mesh-apis.js.map